import { UserNotifyTenure } from '@apis/usersApi.types';
import { CurrencyOption } from '@shared/models/config';
import { PinnedViewKeys } from '@shared/models/saved_view/savedView';
import { Languages } from '@shared/types/common/region';

import { ITeam } from './team/team';
import { UserProfile, UserSociety, UserTwitterConnection } from './user';

/**
 * @deprecated This type alias is deprecated and will be removed in future versions.
 * Please use `UserProfile` directly instead.
 */
export type IUser = UserProfile;

/**
 * @deprecated This type alias is deprecated and will be removed in future versions.
 * Please use `UserSociety` directly instead.
 */
export type IUserSociety = UserSociety;

/**
 * @deprecated This type alias is deprecated and will be removed in future versions.
 * Please use `UserTwitterConnection` directly instead.
 */
export type IUserTwitterConnection = UserTwitterConnection;

export interface IImovirtualConnectionBranch {
  client_id: string;
  client_secret: string;
  id: string;
  label: string;
}

export interface IImovirtualConnection {
  branches: IImovirtualConnectionBranch[];
  enabled: StringBoolean;
  has_tokens: boolean;
}

export enum IUserNotifyType {
  All = 'all',
  Criteria = 'criteria',
  My = 'my',
  None = 'none',
  Team = 'team',
}

export enum IUserNotifyEventsType {
  All = 'all',
  None = 'none',
}

export enum IUserNotifyWhere {
  Desktop = 'desktop',
  Mobile = 'mobile',
  Email = 'Email',
}

export interface INotifyCriteria {
  min_size: number | null;
  max_size: number | null;
  submarkets: number[] | null;
  building_types: string[] | null;
}

export interface INotifySettings {
  acquisitions: {
    type: IUserNotifyType;
    criteria: INotifyCriteria;
    where: INotifyWhere;
  };
  disposals: {
    type: IUserNotifyType;
    criteria: INotifyCriteria;
    tenure: UserNotifyTenure;
    where: INotifyWhere;
  };
  events: {
    type: IUserNotifyEventsType;
    where: INotifyWhere;
  };
  system_notifications: {
    match_comments: NumberBoolean;
    match_request_sent_bcc_on_my_disposals: NumberBoolean;
    match_request_sent_joint_agents: NumberBoolean;
    match_request_sent_notify_team: NumberBoolean;
    pending_match_requests_reminders: NumberBoolean;
    shortlist_emails_cc_on_my_disposals: NumberBoolean;
    shortlist_emails_notify_team: NumberBoolean;
    where: INotifyWhere;
  };
  transactions: {
    type: string;
  };
}

export enum IUserSocietyFilterType {
  None = 'none',
  Team = 'team',
  NoTeam = 'no_team',
}

export interface ISocietyFilters {
  acquisitions: {
    type: IUserSocietyFilterType;
    min_size: number | null;
    max_size: number | null;
    submarkets: number[];
  };
  disposals: {
    type: IUserSocietyFilterType;
    min_size: number | null;
    max_size: number | null;
    submarkets: number[];
  };
}

export interface INotifyWhere {
  desktop: NumberBoolean;
  email: NumberBoolean;
  mobile: NumberBoolean;
}

export interface IPermissions {
  'create-property': boolean;
  'create-requirement': boolean;
  'jll-publish-to-nxt': boolean;
  Comments_Multiple_Internal: boolean;
  Comments_Multiple_Society_Acquisitions: boolean;
  Comments_Multiple_Society_Disposals: boolean;
  Companies_Export_Excel: boolean;
  Contacts_Export_Excel: boolean;
  Dashboard_Requirements_Pipeline: boolean;
  Dashboard_Requirements_Source_Sector: boolean;
  Dashboard_Upcoming_Lease_Events: boolean;
  Dashboard_Upcoming_Viewings: boolean;
  Diary_Lease_Events: boolean;
  Disposal_Insights: boolean;
  Disposals_Class_Of_Use: boolean;
  Disposals_Create_Comp_Schedule: boolean;
  Disposals_Create_PA_Selector: boolean;
  Disposals_Create_Teams_Selector: boolean;
  Disposals_Export_Excel: boolean;
  Disposals_Export_Word: boolean;
  Disposals_Find_Matches_Export_Excel: boolean;
  Disposals_Find_Matches_Export_Word: boolean;
  Disposals_Map_View: boolean;
  Disposals_Navigation_Market_Intel: boolean;
  Disposals_Portal_Interest_Schedule: boolean;
  Disposals_Publish_Microsite: boolean;
  Disposals_Publish_NxT: boolean;
  Disposals_Publish_PDF: boolean;
  Disposals_Publish_Powerpoint: boolean;
  Disposals_Publish_Property_Portals: boolean;
  Disposals_Publish_Twitter: boolean;
  Disposals_Publish_Website: boolean;
  Disposals_Publish_Zoopla_Double_Tick: boolean;
  Email_Campaigns_Interest_Schedule_Update: boolean;
  Email_Campaigns: boolean;
  Floor_Unit_Associated_Landlord: boolean;
  Hide_Data_Is_Private_Company: boolean;
  Hide_Data_Is_Private_Contacts: boolean;
  Hide_Data_Is_Private_Disposals: boolean;
  Hide_Data_Is_Private_Requirements: boolean;
  Hide_Data_Is_Private_Transactions: boolean;
  Hide_Exports: boolean;
  LettingTransaction_Export_Comp_Powerpoint: boolean;
  Location_Favourites: boolean;
  Logo_Agents_Insight: boolean;
  Logo_Custom: boolean;
  Manage_IAS_Events: boolean;
  Navigation_Expanded: boolean;
  Requirements_Agent_Led: boolean;
  Requirements_Create_Dont_Default_Type_Office: boolean;
  Requirements_Create_Hide_Internal_Reference: boolean;
  Requirements_Create_Management: boolean;
  Requirements_Export_Excel: boolean;
  Requirements_Export_Word: boolean;
  Requirements_Kanban: boolean;
  Requirements_Sector_Optional: boolean;
  Requirements_Source_Required: boolean;
  Scheme_Listings_Create: boolean;
  Society_Acquisitions_Export_Excel: boolean;
  Society_Acquisitions_Follow: boolean;
  Society_Acquisitions_Team_Intel: boolean;
  Society_Disposals_Enrich: boolean;
  Society_Disposals_Intel_Rates: boolean;
  Tasks_Viewings: boolean;
  Tasks_Export_Excel: boolean;
  Transactions_Advanced_Filters: boolean;
  Transactions_Export_Excel: boolean;
  Transactions_Export_Word: boolean;
  Transactions_Hide_Upgrade_Message: boolean;
  Transactions_Itza_Calculator: boolean;
  Transactions_Map_View: boolean;
  Transactions_Notifications: boolean;
  Viewings_Export_Excel: boolean;
}

export enum AtlasEnabled {
  Disabled = '0',
  Enabled = '1',
  PreLaunch = '2',
}

export interface Organisation {
  allowed_currencies: CurrencyOption['iso'][];
  atlas_enabled: AtlasEnabled;
  base_currency: CurrencyOption['iso'];
  id: number;
  is_pro: boolean;
  feeds_enabled: any;
  hot_templates: { id: number; name: string }[];
  name: string;
  pdf_templates: any;
  powerpoint_templates: any;
  teams: ITeam[] | null;

  settings: {
    // TODO: Add types for "any"
    address_doctor_pass: any;
    address_doctor_user: any;
    address_doctor: StringBoolean;
    allow_residential: any;
    amenities_specs_enabled: any;
    bayut: any;
    beta_enabled: any;
    billable_name: any;
    billing_notes: any;
    bpiexpresso: any;
    building_types_visible: string[];
    campaign_email_marketing_address_line1: any;
    campaign_email_marketing_city: any;
    campaign_email_marketing_postcode: any;
    campaign_email_sending_as_domain: any;
    campaign_sendgrid_subuser_api_key: any;
    campaign_sendgrid_subuser_name: any;
    campaigns_custom_css: any;
    completely_retail: any;
    custom_map_marker_position: any;
    default_charge_metric: any;
    default_notification_period_lease_breaks: any;
    default_notification_period_lease_end: any;
    default_notification_period_rent_free_periods: any;
    default_notification_period_rent_reduced_periods: any;
    default_notification_period_rent_reviews: any;
    default_rent_metric: any;
    default_size_metric: any;
    dnb_pass: any;
    dnb_user: any;
    dnb: any;
    dubizzle: any;
    each: any;
    eg_property: any;
    egorealestate: any;
    email_marketing_address_city: any;
    email_marketing_address_line1: any;
    email_marketing_address_postcode: any;
    email_marketing_button_bg_colour: any;
    email_marketing_button_text_colour: any;
    email_marketing_campaign_html: any;
    email_marketing_campaign_property_title: any;
    email_marketing_campaigns_enabled: string;
    email_marketing_contacts_cta: any;
    email_marketing_custom_css: any;
    email_marketing_disclaimer: any;
    email_marketing_enabled: string;
    email_marketing_footer_background_colour: any;
    email_marketing_footer_text: any;
    email_marketing_header_bg_colour: any;
    email_marketing_header_block: any;
    email_marketing_header_text_colour: any;
    email_marketing_header_text: any;
    email_marketing_html: any;
    email_marketing_list_message_text: any;
    email_marketing_list_subject: any;
    email_marketing_logo: any;
    email_marketing_main_colour: any;
    email_marketing_message_footer: any;
    email_marketing_message_text: any;
    email_marketing_misrepresentation: any;
    email_marketing_options: EmailMarketingOptions;
    email_marketing_org_name: any;
    email_marketing_secondary_colour: any;
    email_marketing_sending_as_domain: any;
    email_marketing_shortlist_property_title: any;
    email_marketing_subject: any;
    email_marketing_template: any;
    email_marketing_templates: any;
    email_marketing_text_colour: any;
    email_marketing_viewing_message_text: string;
    email_marketing_viewing_subject: string;
    email_shortlist_cc_default: any;
    find_matches_map_enabled: any;
    floor_units_default_columns: any;
    imovirtual: IImovirtualConnection;
    inbound_enquiries_bcc: any;
    inbound: any;
    kyc_enabled: NumberBoolean;
    kyc_only_approved_user_can_edit: NumberBoolean;
    kyc_restrict_publishing_if_not_approved: NumberBoolean;
    main_colour: any;
    map_provider: any;
    marketing_languages: Languages[];
    measurement_standard_visible: string[];
    microsites_credits: any;
    microsites_css: any;
    microsites_enabled: any;
    microsites_logo_url: any;
    microsites_unlimited: any;
    misoficinas: any;
    notes: any;
    novaloca: any;
    olm_can_publish_to: any;
    on_the_market: any;
    property_pal: any;
    property_news: any;
    pdf_enabled: any;
    pdf_logo_url: any;
    pdf_main_colour: any;
    pdf_secondary_colour: any;
    pdf_tertiary_colour: any;
    portals_enabled: any;
    pro_mode: any;
    property_finder: any;
    publish_to_radius_visible: number;
    publish_to_twitter_visible: any;
    radius: any;
    realla: any;
    reports_disposals_enabled: any;
    reports_disposals_logo: any;
    reports_disposals_primary_colour: any;
    reports_disposals_secondary_colour: any;
    reports_disposals_template: any;
    reports_letting_transactions_enabled: any;
    reports_letting_transactions_logo: any;
    reports_letting_transactions_primary_colour: any;
    reports_letting_transactions_secondary_colour: any;
    reports_letting_transactions_template: any;
    reports_requirements_enabled: any;
    reports_requirements_logo: any;
    reports_requirements_primary_colour: any;
    reports_requirements_secondary_colour: any;
    reports_requirements_template: any;
    reports_shortlist_powerpoint_cover_image: any;
    reports_shortlist_powerpoint_map_marker_bg_colour: any;
    reports_shortlist_powerpoint_primary_logo: any;
    reports_shortlist_powerpoint_secondary_logo: any;
    reports_shortlist_powerpoint_templates: ItemWithId[];
    requirement_find_matches_default_our_instructions: NumberBoolean;
    requirements_search_radius_enabled: any;
    rightmove: any;
    search_prop_domain_id: any;
    search_prop_enabled: any;
    search_prop_general_additional_emails: any;
    search_prop_property_additional_emails: any;
    search_prop_subdomain: any;
    secondary_colour: any;
    sectors_visible: string[];
    sendgrid_keys_migrated: any;
    sendgrid_subuser_api_key: any;
    sendgrid_subuser_name: any;
    sending_domain_preference: any;
    setup_mode: any;
    shortlist_microsites_credits: any;
    shortlist_microsites_css: any;
    shortlist_microsites_enabled: any;
    shortlist_microsites_logo_url: any;
    shortlist_microsites_unlimited: any;
    societies_counts: any;
    societies: any;
    task_types_visible: string[];
    tasks_enabled: any;
    template: any;
    tertiary_colour: any;
    transaction_notifications_enabled: any;
    transactions_require_disposals: any;
    twitter: {
      accounts: IUserTwitterConnection[];
      enabled: StringBoolean;
    };
    use_classes_visible: string[];
    website_image_url_type: any;
    website_preview_enabled: any;
    website_xml_enabled: any;
    website_xml_type: any;
    website_xml_url: any;
    zoopla_rtf: any;
    zoopla: any;
  };
}

export interface EmailMarketingOptions {
  rent?: string;
  size?: string;
  summary?: string;
  price?: string;
}

export interface CommonAuthenticatable {
  backgroundColour: string;
  iasMember: boolean;
  image: string;
  name: string;
  portalContactId: number;
  pro: boolean;
  textColour: string;
  userId: number;
}

export const defaultUser: UserProfile = {
  [PinnedViewKeys.advisory]: [],
  [PinnedViewKeys.crm_companies]: [],
  [PinnedViewKeys.crm_contacts]: [],
  [PinnedViewKeys.disposal_insights]: [],
  [PinnedViewKeys.disposals_dev_pipeline]: [],
  [PinnedViewKeys.disposals_instruction_availability]: [],
  [PinnedViewKeys.disposals_investment]: [],
  [PinnedViewKeys.disposals_market_availability]: [],
  [PinnedViewKeys.disposals_pitching]: [],
  [PinnedViewKeys.disposals]: [],
  [PinnedViewKeys.eg_rankings]: [],
  [PinnedViewKeys.email_campaigns]: [],
  [PinnedViewKeys.fees]: [],
  [PinnedViewKeys.landlord_disposals]: [],
  [PinnedViewKeys.letting_transaction_insights]: [],
  [PinnedViewKeys.letting_transactions]: [],
  [PinnedViewKeys.properties]: [],
  [PinnedViewKeys.radius_lettings_transactions]: [],
  [PinnedViewKeys.radius_sales_transactions]: [],
  [PinnedViewKeys.atlas_lettings_transactions]: [],
  [PinnedViewKeys.atlas_sales_transactions]: [],
  [PinnedViewKeys.requirement_insights]: [],
  [PinnedViewKeys.requirements]: [],
  [PinnedViewKeys.sale_transaction_insights]: [],
  [PinnedViewKeys.sale_transactions_investment]: [],
  [PinnedViewKeys.sale_transactions_occupational]: [],
  [PinnedViewKeys.sale_transactions]: [],
  [PinnedViewKeys.society_acquisition_insights]: [],
  [PinnedViewKeys.society_disposal_insights]: [],
  [PinnedViewKeys.society_disposals]: [],
  [PinnedViewKeys.viewing_insights]: [],
  assets: [],
  disposalPowerpointExportEnabled: false,
  email: 'anony.mouse@tlgd.co.uk',
  forename: 'Anony',
  id: 1,
  image: {
    url: null,
  },
  lastLogin: '2023-09-20 12:56:42',
  leases_eg_leaderboard_region: null,
  linkedin: null,
  mobile: '07777777777',
  name: 'Anony Mouse',
  notify_settings: {
    acquisitions: {
      type: IUserNotifyType.None,
      criteria: {
        min_size: null,
        max_size: null,
        submarkets: [],
        building_types: [],
      },
      where: {
        email: 0,
        desktop: 0,
        mobile: 0,
      },
    },
    disposals: {
      type: IUserNotifyType.None,
      criteria: {
        min_size: null,
        max_size: null,
        submarkets: [],
        building_types: [],
      },
      tenure: UserNotifyTenure.Both,
      where: {
        email: 0,
        desktop: 0,
        mobile: 0,
      },
    },
    events: {
      type: IUserNotifyEventsType.None,
      where: {
        email: 0,
        desktop: 0,
        mobile: 0,
      },
    },
    transactions: {
      type: 'team',
    },
    system_notifications: {
      pending_match_requests_reminders: 0,
      match_comments: 0,
      match_request_sent_joint_agents: 0,
      match_request_sent_notify_team: 0,
      match_request_sent_bcc_on_my_disposals: 0,
      shortlist_emails_cc_on_my_disposals: 1,
      shortlist_emails_notify_team: 1,
      where: {
        email: 1,
        desktop: 1,
        mobile: 1,
      },
    },
  },
  nxtCanPublishTo: false,
  nxtEnabled: false,
  onboarding: {
    notifications: true,
    profile_confirmed: false,
    publish_listing: true,
  },
  organisation_id: 1,
  organisation: undefined,
  permissions: null,
  pivot: {
    role: null,
    sort: null,
  },
  position: null,
  preferred_requirement_columns: [],
  pro: false,
  sales_eg_leaderboard_region: null,
  setup_mode: false,
  societies: [],
  society_filters: {
    acquisitions: {
      type: IUserSocietyFilterType.Team,
      min_size: null,
      max_size: null,
      submarkets: [],
    },
    disposals: {
      type: IUserSocietyFilterType.Team,
      min_size: null,
      max_size: null,
      submarkets: [],
    },
  },
  society_regions_string: '',
  society_regions: [5],
  society_user_id: null,
  surname: 'Mouse',
  teams: [],
  tel: '01234567890',
  terms: true,
  theme: {
    backgroundColour: '#CFDDFA',
    textColour: '#487EF5',
  },
  twitter_connection: null,
  twitter: null,
  view_interest_with_requirement_enabled: false,
  visible_interest_fields: [],
  visible_requirement_fields: [],
};
